import { toast } from "react-toastify";
import { endpoints } from "../../../configs/Settings";
import { HTTPResponseStatus } from "../../../utils/enum";
import { useAuth } from "../../../utils/useAuth";
import { useAxios } from "../../../utils/useAxios";
import { Task } from "fhir/r5";

interface IProps {
  id?: string;
  status?: string[];
  owner?: string;
  requester?: string;
  patient?: string;
  sort?: string;
  subject?: string;
  lastUpdatedGe?: string; // Filtro de datas: _lastUpdated=ge{date}
  authoredOnGe?: string; // Filtro por authored-on=ge{date}
  /** Substitui o `_count` padrão (1000). Omitir mantém o contrato atual. */
  count?: number;
  /** Segue `link.next` e devolve um único Bundle. Default: só a primeira página. */
  fetchAllPages?: boolean;
}

const MAX_TASK_PAGES = 20;

function nextBundleUrl(bundle: {
  link?: Array<{ relation?: string; url?: string }>;
}): string | undefined {
  const url = bundle?.link?.find((link) => link.relation === "next")?.url?.trim();
  return url || undefined;
}

async function requestTask(url: string) {
  const headers = {
    Accept: "*/*",
    Authorization: await useAuth.getUserToken(),
    "Content-Type": "application/fhir+json",
  };

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Success) {
        throw new Error(`Get Task: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("GetTask ", error);
      // toast.error(`GetTask Error: ${error?.response?.status}`);
      throw new Error();
    });

  if (response) return response;
}

/**
 * @param id task id
 * @param status draft | requested | received | accepted | rejected | ready | cancelled | in-progress | on-hold | failed | completed | entered-in-error
 * @param owner reference do profissional responsável pela tarefa
 * @param requester reference do solicitante da tarefa
 * @param patient reference do paciente relacionado à tarefa
 * @param sort task field, to sort in reverse use the character -, e.g. -authoredOn
 * @param subject reference do paciente ou profissional responsável pela tarefa
 * @returns Bundle contendo Task ou Task específica
 */
export async function GetTask({
  id,
  status,
  owner,
  requester,
  patient,
  sort,
  subject,
  lastUpdatedGe,
  authoredOnGe,
  count,
  fetchAllPages,
}: IProps): Promise<Task | any> {
  let url = `${endpoints.WORKFLOWFHIR}/Task`;
  if (id) url = `${url}/${id}`;
  else url = `${url}?_count=${count ?? 1000}`;
  
  if (status) url = `${url}&status=${status.join(",")}`;
  if (owner) url = `${url}&owner=${owner}`;
  if (requester) url = `${url}&requester=${requester}`;
  if (patient) url = `${url}&patient=${patient}`;
  if (sort) url = `${url}&_sort=${sort}`;
  if (lastUpdatedGe) url = `${url}&_lastUpdated=ge${lastUpdatedGe}`;
  if (authoredOnGe) url = `${url}&authored-on=ge${authoredOnGe}`;
  if (subject) url = `${url}&subject=${subject}`;

  const first = await requestTask(url);
  if (!fetchAllPages || id || !first) return first;

  const entries = Array.isArray(first.entry) ? [...first.entry] : [];
  const seen = new Set<string>();
  let nextUrl = nextBundleUrl(first);
  let pages = 1;

  while (nextUrl && pages < MAX_TASK_PAGES && !seen.has(nextUrl)) {
    seen.add(nextUrl);
    const page = await requestTask(nextUrl);
    if (Array.isArray(page?.entry)) entries.push(...page.entry);
    nextUrl = nextBundleUrl(page);
    pages += 1;
  }

  return { ...first, entry: entries };
}